import React from 'react';
import { Task } from '@/app/task-bank/public-actions';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useTaskSelection } from './task-selection/use-task-selection';
import { TaskFilters } from './task-selection/task-filters';
import { TaskCardRenderer } from './task-selection/task-card-renderer';

interface TaskSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectTask: (task: Task) => void;
  taskType: 'task1' | 'task2';
  selectedTaskId?: string | null;
}

export function TaskSelectionModal({
  open,
  onOpenChange,
  onSelectTask,
  taskType,
  selectedTaskId,
}: TaskSelectionModalProps) {
  const {
    tasks,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    task1TypeFilter,
    setTask1TypeFilter,
    visualTypeFilter,
    setVisualTypeFilter,
    questionTypeFilter,
    setQuestionTypeFilter,
    keywords,
    selectedKeywordId,
    toggleKeyword,
    showAllKeywords,
    setShowAllKeywords,
    keywordSearch,
    setKeywordSearch,
    isLoadingMore,
    clearFilters,
  } = useTaskSelection({ open, taskType });

  const handleTaskSelect = (task: Task) => {
    onSelectTask(task);
    onOpenChange(false);
  };

  const hasActiveFilters =
    searchQuery ||
    selectedKeywordId ||
    task1TypeFilter !== 'all' ||
    visualTypeFilter !== 'all' ||
    questionTypeFilter !== 'all';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='min-w-3xl h-[80vh] flex flex-col'>
        <DialogHeader>
          <DialogTitle>
            Select a {taskType === 'task1' ? 'Task 1' : 'Task 2'}
          </DialogTitle>
          <DialogDescription>
            Choose a task from the IELTS{' '}
            {taskType === 'task1' ? 'Academic Task 1' : 'Writing Task 2'} bank
          </DialogDescription>
        </DialogHeader>

        <TaskFilters
          taskType={taskType}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          task1TypeFilter={task1TypeFilter}
          onTask1TypeChange={setTask1TypeFilter}
          visualTypeFilter={visualTypeFilter}
          onVisualTypeChange={setVisualTypeFilter}
          questionTypeFilter={questionTypeFilter}
          onQuestionTypeChange={setQuestionTypeFilter}
          keywords={keywords}
          selectedKeywordId={selectedKeywordId}
          onToggleKeyword={toggleKeyword}
          showAllKeywords={showAllKeywords}
          onToggleShowAllKeywords={setShowAllKeywords}
          keywordSearch={keywordSearch}
          onKeywordSearchChange={setKeywordSearch}
        />

        <div className='flex-1 overflow-hidden border rounded-md mt-4'>
          <ScrollArea className='h-full'>
            {isLoading && tasks.length === 0 ? (
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 p-4'>
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className='h-48 w-full' />
                ))}
              </div>
            ) : error ? (
              <div className='flex items-center justify-center h-full p-4'>
                <p className='text-destructive'>{error}</p>
              </div>
            ) : tasks.length === 0 ? (
              <div className='flex flex-col items-center justify-center h-full p-4'>
                <p className='text-muted-foreground'>No tasks found</p>
                {hasActiveFilters && (
                  <Button
                    variant='outline'
                    className='mt-2'
                    onClick={clearFilters}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            ) : (
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 p-4'>
                {tasks.map((task) => (
                  <TaskCardRenderer
                    key={task.id}
                    task={task}
                    taskType={taskType}
                    selectedTaskId={selectedTaskId}
                    onSelectTask={handleTaskSelect}
                  />
                ))}
                {isLoadingMore && (
                  <div className='col-span-2 flex justify-center p-4'>
                    <Skeleton className='h-10 w-10 rounded-full' />
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
